'use client';
import Button from '@/components/Button';
import ChoiceButton from '@/components/ChoiceButton';
import Input from '@/components/Input';
import { Modal } from '@/components/Modal/Modal';
import Tooltip from '@/components/Tooltip';
import ArrowLeftCircle from '@/components/icons/ArrowLeftCircle';
import { NotifsContext } from '@/contexts';
import useNavigate from '@/hooks/useNavigate';
import {
  useEditEmployerMutation,
  useLazyGetEmployerQuery,
} from '@/services/API';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setSte } from '@/store/slices/steSlice';
import form from '@/styles/form.module.css';
import { AuthnMethod, EmployerType } from '@/types';
import { InputText, getEmptyError } from '@/types/Input';
import { useContext, useEffect, useState } from 'react';
import styles from './page.module.css';

type FormEmployer = {
  siren: InputText;
  name: InputText;
  tag: InputText;
  employer_type: { value: EmployerType };
  api_organization_id: InputText;
  authn_method: { value: AuthnMethod };
};

export default function Page() {
  const id = useAppSelector((state) => state.selectedSte.societe.id);
  const siren = useAppSelector((state) => state.selectedSte.societe.siren);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [employer, setEmployer] = useState<FormEmployer>();
  const [editEmployer, { isLoading }] = useEditEmployerMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { addToast } = useContext(NotifsContext);

  const [getEmployer, { data: oldEmployer }] = useLazyGetEmployerQuery();

  useEffect(() => {
    getEmployer(siren);
  }, []);

  useEffect(() => {
    if (oldEmployer) {
      setEmployer({
        siren: {
          value: oldEmployer.siren,
          valid: true,
          label: 'SIREN *',
          placeholder: '',
          validator: (value: string) => value !== '',
          getErrorMessage: getEmptyError,
          errorMessage: '',
        },
        name: {
          value: oldEmployer.name,
          valid: true,
          label: 'Raison sociale *',
          placeholder: '',
          validator: (value: string) => value !== '',
          getErrorMessage: getEmptyError,
          errorMessage: '',
        },
        tag: {
          value: oldEmployer.tag,
          valid: true,
          label: 'Tag *',
          placeholder: '',
          validator: (value: string) => value !== '',
          getErrorMessage: getEmptyError,
          errorMessage: '',
        },
        employer_type: {
          value: oldEmployer.employer_type,
        },
        api_organization_id: {
          value: oldEmployer.api_organization_id,
          valid: true,
          label: 'ID de l’organisation API d’administration',
          placeholder: '',
          validator: (value: string) => value !== '',
          getErrorMessage: getEmptyError,
          errorMessage: '',
        },
        authn_method: {
          value: oldEmployer.authn_method,
        },
      });
    }
  }, [oldEmployer]);

  const handleSubmit = () => {
    if (!employer) return;
    editEmployer({
      id,
      body: {
        tag: employer.tag.value,
        employer_type: employer.employer_type.value,
        api_organization_id: employer.api_organization_id.value,
        authn_method: employer.authn_method.value,
      },
    })
      .unwrap()
      .then(() => {
        addToast &&
          addToast({
            toastType: 'success',
            children: 'Confirmation de modification.',
          });
        //update employer
        getEmployer(siren)
          .unwrap()
          .then((data) => {
            const value = {
              label: data.name,
              value: data.name,
              ...data,
            };
            dispatch(setSte(value));
          });
      })
      .catch(() => {
        addToast &&
          addToast({
            toastType: 'error',
            children: `Une erreur est survenue.
          Rééssayer ultérieurement.`,
          });
      })
      .finally(() => setOpenConfirmModal(false));
  };

  if (!employer || !oldEmployer) return <></>;

  return (
    <div className={`flex-column gap-l ${form.content}`}>
      <Button
        variant="ghost"
        icon={<ArrowLeftCircle />}
        onClick={() => navigate(-1)}
      >
        Retour
      </Button>
      <form className={`${form.form} gap-l`}>
        <h3 className={form.title}>Paramétrer la société</h3>
        <Input
          disabled={true}
          label={employer.name.label}
          placeholder={employer.name.placeholder}
          value={employer.name.value}
        />
        <Input
          disabled={true}
          label={employer.siren.label}
          placeholder={employer.siren.placeholder}
          value={employer.siren.value}
          helperText="9 caractères alphanumérique"
        />
        <div className="flex-column gap-s">
          <div className="flex-row gap-s">
            <p>Méthode d’authentification</p>
            <Tooltip
              message={
                <>
                  Locale = authentification portée par PING IDENTITY.
                  <br />
                  Fédérée = authentification portée par le partenaire.
                </>
              }
            />
          </div>
          <div className={`flex-row gap-m ${styles.choices}`}>
            <ChoiceButton
              type="button"
              onClick={(e) => {
                setEmployer({
                  ...employer,
                  authn_method: { value: 'LOCAL' },
                });
              }}
              selected={employer.authn_method.value === 'LOCAL'}
            >
              Locale
            </ChoiceButton>
            <ChoiceButton
              type="button"
              onClick={(e) => {
                setEmployer({
                  ...employer,
                  authn_method: { value: 'FEDERATED' },
                });
              }}
              selected={employer.authn_method.value === 'FEDERATED'}
            >
              Fédérée
            </ChoiceButton>
          </div>
        </div>
        <div className="flex-column gap-s">
          <p>Type *</p>
          <div className={`flex-row gap-m ${styles.choices}`}>
            <ChoiceButton
              type="button"
              onClick={(e) => {
                setEmployer({
                  ...employer,
                  employer_type: { value: 'COMPANY' },
                });
              }}
              selected={employer.employer_type.value === 'COMPANY'}
            >
              Société
            </ChoiceButton>
            <ChoiceButton
              type="button"
              onClick={(e) => {
                setEmployer({
                  ...employer,
                  employer_type: { value: 'EVENT' },
                });
              }}
              selected={employer.employer_type.value === 'EVENT'}
            >
              Évènement
            </ChoiceButton>
          </div>
        </div>
        <Input
          label={employer.tag.label}
          maxLength={50}
          value={employer.tag.value}
          placeholder={employer.tag.placeholder}
          helperText="Mot-clé permettant d'identifier la société. Mot-clé utilisé pour constituer l'identifiant utilisateur d'accès à nos applications."
          onChange={(e) => {
            const newValue = (e?.target?.value ?? '').replaceAll(
              /[^a-zA-Z.0-9 \-_&]/g,
              '',
            );
            const valid = employer.tag.validator(newValue);
            setEmployer({
              ...employer,
              tag: {
                ...employer.tag,
                value: newValue,
                valid,
                errorMessage: '',
              },
            });
          }}
        />
        <Input
          label={employer.api_organization_id.label}
          maxLength={50}
          value={employer.api_organization_id.value}
          placeholder={employer.api_organization_id.placeholder}
          helperText="Ce partenaire utilise l'API d'administration pour gérer ses employés. Renseignez ici l'identifiant technique utilisé par le Partenaire pour accéder à notre API. Identifiant donné par la DSI PF France."
          onChange={(e) => {
            const valid = employer.api_organization_id.validator(
              e.target.value,
            );
            setEmployer({
              ...employer,
              api_organization_id: {
                ...employer.api_organization_id,
                value: e.target.value,
                valid,
                errorMessage: '',
              },
            });
          }}
        />
        <p className="xs">* Champs obligatoires</p>
        <Button
          type="button"
          disabled={
            !employer.name.valid || !employer.siren.valid || !employer.tag.valid
          }
          onClick={(e) => {
            setOpenConfirmModal(true);
          }}
        >
          Paramétrer la société
        </Button>
      </form>

      <Modal
        primaryBtnProps={{
          loading: isLoading,
          children: 'Confirmer',
          onClick: handleSubmit,
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenConfirmModal(false),
        }}
        title="Voulez-vous modifier la société ?"
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
      />
    </div>
  );
}
